import classNames from 'classnames'
import React from 'react'

import { getUrlParam } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { useDropInviteToken } from 'onboarding/hooks/registration/useDropInviteToken'

import styles from './RegistrationHpidSignIn.module.scss'

/**
 * Copy is intentionally kept in one place. Both registration login screens are
 * English-only today (the wizard behind them is translated, the login screens
 * never were), so moving these strings onto translation codes is a single edit
 * here once the backend scope carries them.
 *
 * The programme name is the title, so it says what this screen is without a
 * separate "start your registration" line. That leaves the body text to carry
 * the one thing the button cannot: that an account can be created here, and
 * which account has to be used to resume an existing registration.
 */
const COPY = {
  startDescription: 'Please sign in or sign up with your HP ID account',
  continueDescription: 'Sign in with the HP ID this registration was started with.',
  requestLabel: 'Registration ID',
  action: 'Sign in with HP ID',
  actionProgress: 'Opening HP ID',
}

interface IRegistrationHpidSignInProps {
  onSignIn: () => void
  progress: boolean
  /** Titles the screen, e.g. "Business Partner Registration". */
  programName: string
  /** The host page owns the brand mark: BP puts it here, APL already has one in its header. */
  logo?: React.ReactNode
  className?: string
}

export const RegistrationHpidSignIn = ({
  onSignIn,
  progress,
  programName,
  logo,
  className,
}: IRegistrationHpidSignInProps) => {
  const { hasToken } = useDropInviteToken()
  const requestId = getUrlParam('rid')

  // The token is being dropped from the URL; sign-in must not start against a
  // path that is about to change.
  if (hasToken) {
    return <Loader />
  }

  const isContinuing = Boolean(requestId)

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.heading}>
        {logo}
        <h2 className={styles.title}>{programName}</h2>
      </div>

      <p className={styles.description}>
        {isContinuing ? COPY.continueDescription : COPY.startDescription}
      </p>

      {isContinuing ? (
        <div className={styles.request}>
          <span className={styles.request__label}>{COPY.requestLabel}</span>
          <span className={styles.request__value}>{requestId}</span>
        </div>
      ) : null}

      <Button
        className={styles.action}
        onClick={() => onSignIn()}
        disabled={progress}
        loading={progress}
      >
        {progress ? COPY.actionProgress : COPY.action}
      </Button>
    </div>
  )
}
